/* eslint-disable radix */
import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react'
import Grid from '@material-ui/core/Grid'
import { Typography } from '@material-ui/core'
import AddIcon from '@material-ui/icons/AddCircleOutlineOutlined'

import moment, { Moment } from 'config/moment'
import { Dialog, UploadButton, snackbar } from 'components'
import { useCheckFormErrors, useApiCall } from 'hooks'
import { UserContext } from 'contexts'
import { getUserRole, Role } from 'utils/roles'
import { CustomFormControlLabel } from 'components/CustomFormControlLabel'
import { CustomCheckbox } from 'components/CustomCheckbox'
import { PrizeCategory } from 'consts/prize'
import { TabPanel } from 'components/Dialog/TabPanel'
import CustomTextField from 'components/CustomTextField'
import { ConfigUtils } from 'utils/config'

import { PrizeDateRangeFields } from '../../../Memberships/Prizes/components/PrizeDateRangeFields'
import defaultIcon from '../../../../../assets/images/default-icon.png'
import { PartnerIntegration } from '../../../components/PartnerIntegration'
import { useStyles } from '../../../ManualRewards/components/CreateManualRewardDialog/styles'
import {
  campaignDialogStyle,
  voucherDialogContentStyle,
} from '../../../Campaigns/components/campaignDialogStyle'
import {
  getVoucherDialogTabs,
  getVoucherPopUpTabIndex,
  isCampaignPopUpsEnabled,
  shouldShowPushImageUpload,
} from '../../../Campaigns/components/campaignFeatureFlags'
import { fetchPushUserCount } from '../../../Campaigns/api'
import { DataParams, DataResponse } from '../../../Campaigns/types'
import { createVoucher, uploadPopUpImage } from '../../api'
import { Tag } from '../../../Tags/types'
import {
  CreateVoucherPayload,
  CreateVoucherResponse,
  UploadPopUpImageResponse,
  Voucher,
} from '../../types'
import { mergeEmails, parseEmailsFromCsvFile } from '../../voucherEmailUtils'
import { TagOption, VoucherSegmentationTab } from '../VoucherSegmentationTab'
import { createNotification } from '../../../Notifications/api'
import { CreateNotificationPayload, CreateNotificationResponse } from '../../../Notifications/types'

interface ValidationData {
  toteatEnabled: boolean
  tucanCategory?: PrizeCategory
  toteatCategory?: PrizeCategory
  pushMessage?: boolean
  activePopUp?: boolean
  isCampaignPopUpsEnabled?: boolean
}

const rules = {
  listingTitle: [
    {
      validate: (value: string) => value.trim().length > 0,
      message: 'El título es requerido',
      validateEmpty: true,
    },
    {
      validate: (value: string) => value.length <= 70,
      message: 'Máximo 70 caracteres',
    },
  ],
  shortTitle: [
    {
      validate: (shortTitle: string) => shortTitle.trim().length > 0,
      message: 'La descripción es requerida',
      validateEmpty: true,
    },
    {
      validate: (shortTitle: string) => shortTitle.length <= 40,
      message: 'Máximo 40 caracteres',
    },
  ],
  benefitTitle: [
    {
      validate: (value: string) => value.trim().length > 0,
      message: 'El título del beneficio es requerido',
      validateEmpty: true,
    },
    {
      validate: (value: string) => value.length <= 70,
      message: 'Máximo 70 caracteres',
    },
  ],
  additionalText: [
    {
      validate: (value: string) => value.trim().length > 0,
      message: 'El título secundario es requerido',
      validateEmpty: true,
    },
    {
      validate: (value: string) => value.length <= 100,
      message: 'Máximo 100 caracteres',
    },
  ],
  icon: [],
  prizeStartDate: [
    {
      validate: (value: Moment | null) => !!value,
      message: 'La fecha de inicio es requerida',
      validateEmpty: true,
    },
  ],
  prizeExpiration: [
    {
      validate: (value: Moment | null) => !!value,
      message: 'La fecha de vencimiento es requerida',
      validateEmpty: true,
    },
  ],
  pushTitle: [
    {
      validate: (title: string, data?: ValidationData) => {
        if (data?.pushMessage && !title.trim()) return false
        return true
      },
      message: 'El título push es requerido',
      validateEmpty: true,
    },
    {
      validate: (title: string) => !title || title.length <= 30,
      message: 'Máximo 30 caracteres',
    },
  ],
  pushDescription: [
    {
      validate: (description: string, data?: ValidationData) => {
        if (data?.pushMessage && !description.trim()) return false
        return true
      },
      message: 'La descripción push es requerida',
      validateEmpty: true,
    },
    {
      validate: (description: string) => !description || description.length <= 100,
      message: 'Máximo 100 caracteres',
    },
  ],
  toteatBenefitId: [
    {
      validate: (value: string, data: ValidationData) => {
        const { toteatEnabled, toteatCategory } = data
        if (toteatEnabled && toteatCategory === PrizeCategory.Checkout) {
          return value.trim().length > 0
        }
        return true
      },
      message: 'El ID de beneficio POS es requerido',
      validateEmpty: true,
    },
    {
      validate: (value: string) => value.trim().length <= 50,
      message: 'Máximo 50 caracteres',
    },
  ],
  skusList: [
    {
      validate: (skusList: string[], data: ValidationData) => {
        const { toteatEnabled, toteatCategory } = data
        if (toteatEnabled && toteatCategory === PrizeCategory.General) {
          return skusList.length > 0
        }
        return true
      },
      message: 'El SKU es requerido',
    },
  ],
  popUpImageId: [
    {
      validate: (value: string | undefined, data: ValidationData) => {
        const { activePopUp, isCampaignPopUpsEnabled: popUpsEnabled } = data
        if (popUpsEnabled && activePopUp && (!value || value.trim().length === 0)) {
          return false
        }
        return true
      },
      message: 'La imagen del pop-up es requerida',
      validateEmpty: true,
    },
  ],
}

interface CreateVoucherDialogProps {
  handleClose: () => void
  onDone: (newVoucher: Voucher) => void
  tags: Tag[]
}

const CreateVoucherDialog: React.FC<CreateVoucherDialogProps> = ({ handleClose, onDone, tags }) => {
  const classes = useStyles()
  const tabs = getVoucherDialogTabs()
  const popUpTabIndex = getVoucherPopUpTabIndex()
  const campaignPopUpsEnabled = isCampaignPopUpsEnabled()
  const { admin } = useContext(UserContext)
  const userRole = getUserRole(admin)
  const isMarketing = userRole === Role.Marketing

  const [toteatBenefitId, setToteatBenefitId] = useState<string>('')
  const [currentSku, setCurrentSku] = useState<string>('')
  const [skusList, setSkusList] = useState<string[]>([])
  const [storesEnabled, setStoresEnabled] = useState(true)
  const [tucanEnabled, setTucanEnabled] = useState(false)
  const [toteatEnabled, setToteatEnabled] = useState(false)
  const [toteatCategory, setToteatCategory] = useState<PrizeCategory | undefined>(
    PrizeCategory.Checkout,
  )
  const [tucanCategory, setTucanCategory] = useState<PrizeCategory | undefined>(
    PrizeCategory.Checkout,
  )
  const [listingTitle, setListingTitle] = useState('')
  const [shortTitle, setShortTitle] = useState('')
  const [benefitTitle, setBenefitTitle] = useState('')
  const [additionalText, setAdditionalText] = useState('')
  const [icon, setIcon] = useState<File | null>(null)
  const [iconPreview, setIconPreview] = useState('')
  const [image, setImage] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState('')
  const [pushMessage, setPushMessage] = useState(false)
  const [pushTitle, setPushTitle] = useState('')
  const [pushDescription, setPushDescription] = useState('')
  const [active, setActive] = useState(!isMarketing)
  const [activePopUp, setActivePopUp] = useState(false)
  const [popUpImageId, setPopUpImageId] = useState('')
  const [popUpImagePreview, setPopUpImagePreview] = useState('')
  const [isUploadingPopUpImage, setIsUploadingPopUpImage] = useState(false)
  const [tagsSelected, setTagsSelected] = useState<TagOption[]>([])
  const [excludedTagsSelected, setExcludedTagsSelected] = useState<TagOption[]>([])
  const [allTagsAreRequired, setAllTagsAreRequired] = useState(false)
  const [emailChips, setEmailChips] = useState<string[]>([])
  const [emailCsvFile, setEmailCsvFile] = useState<File | null>(null)
  const [csvEmails, setCsvEmails] = useState<string[]>([])
  const [csvEmailCount, setCsvEmailCount] = useState(0)
  const [prizeStartDate, setPrizeStartDate] = useState<Moment | null>(null)
  const [prizeExpiration, setPrizeExpiration] = useState<Moment | null>(null)
  const [hasPartnerErrors, setHasPartnerErrors] = useState(false)
  const [userCount, setUserCount] = useState<number | null>(null)
  const [tabIndex, setTabIndex] = useState(0)
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)

  const isEcardEnabled = process.env.REACT_APP_ECARD_ENABLED === 'true'
  const hasAnyIntegration = ConfigUtils.hasAnyIntegrationEnabled()
  const tagsForSelect = tags.map(el => ({ label: el.title, value: el.id }))

  const [createVoucherApi, isLoading] = useApiCall<CreateVoucherPayload, CreateVoucherResponse>(
    createVoucher,
  )
  const [createNotificationApi] = useApiCall<CreateNotificationPayload, CreateNotificationResponse>(
    createNotification,
  )
  const [uploadPopUpImageApi] = useApiCall<File, UploadPopUpImageResponse>(uploadPopUpImage)
  const [requestData, isRequestingData] = useApiCall<DataParams, DataResponse>(fetchPushUserCount)

  const clearEmailSegmentation = () => {
    setEmailChips([])
    setEmailCsvFile(null)
    setCsvEmails([])
    setCsvEmailCount(0)
  }

  const clearTagSegmentation = () => {
    setTagsSelected([])
    setExcludedTagsSelected([])
    setAllTagsAreRequired(false)
  }

  const handleSetTags = async (selectedTags: TagOption[]) => {
    const excludedIds = new Set(excludedTagsSelected.map(tag => tag.value))
    const nextTags = selectedTags.filter(tag => !excludedIds.has(tag.value))
    setTagsSelected(nextTags)
    if (nextTags.length > 0 || excludedTagsSelected.length > 0) {
      clearEmailSegmentation()
    }
  }

  const handleSetExcludedTags = async (selectedTags: TagOption[]) => {
    const includedIds = new Set(tagsSelected.map(tag => tag.value))
    const nextTags = selectedTags.filter(tag => !includedIds.has(tag.value))
    setExcludedTagsSelected(nextTags)
    if (nextTags.length > 0 || tagsSelected.length > 0) {
      clearEmailSegmentation()
    }
  }

  const handleEmailChipsChange = (emails: string[]) => {
    setEmailChips(emails)
    if (emails.length > 0) {
      clearTagSegmentation()
      setEmailCsvFile(null)
      setCsvEmails([])
      setCsvEmailCount(0)
    }
  }

  const handleAllTagsAreRequiredChange: React.ChangeEventHandler<HTMLInputElement> = () => {
    setAllTagsAreRequired(!allTagsAreRequired)
  }

  const handleFetchUserCount = async () => {
    if (!tagsSelected.length && !excludedTagsSelected.length) {
      setUserCount(null)
      return
    }

    try {
      const tagsSelectedId = tagsSelected.map(tag => tag.value)
      const data = await requestData({
        tagIds: tagsSelectedId,
        excludedTagIds: excludedTagsSelected.map(tag => tag.value),
        allTagsAreRequired,
      })
      setUserCount(data.count)
    } catch (err) {
      console.error(err)
      snackbar.show('No se pudo calcular la cantidad de usuarios. Intente de nuevo.')
    }
  }

  useEffect(() => {
    handleFetchUserCount()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagsSelected, excludedTagsSelected, allTagsAreRequired])

  const hasValidSegmentation = useCallback(
    () =>
      tagsSelected.length > 0 ||
      excludedTagsSelected.length > 0 ||
      emailChips.length > 0 ||
      csvEmails.length > 0,
    [tagsSelected, excludedTagsSelected, emailChips, csvEmails],
  )

  const getRequiredFields = useCallback(() => {
    const base = [
      'listingTitle',
      'shortTitle',
      'benefitTitle',
      'additionalText',
      'icon',
      'prizeStartDate',
      'prizeExpiration',
    ]
    if (pushMessage) base.push('pushTitle', 'pushDescription')
    if (campaignPopUpsEnabled && activePopUp) base.push('popUpImageId')
    return base
  }, [pushMessage, campaignPopUpsEnabled, activePopUp])

  const fields = useMemo(
    () => ({
      listingTitle,
      shortTitle,
      benefitTitle,
      additionalText,
      icon,
      pushMessage,
      pushTitle,
      pushDescription,
      toteatEnabled,
      toteatBenefitId,
      skusList,
      tucanCategory,
      toteatCategory,
      prizeStartDate,
      prizeExpiration,
      activePopUp,
      popUpImageId,
      isCampaignPopUpsEnabled: campaignPopUpsEnabled,
    }),
    [
      listingTitle,
      shortTitle,
      benefitTitle,
      additionalText,
      icon,
      pushMessage,
      pushTitle,
      pushDescription,
      toteatEnabled,
      toteatBenefitId,
      skusList,
      tucanCategory,
      toteatCategory,
      prizeStartDate,
      prizeExpiration,
      activePopUp,
      popUpImageId,
      campaignPopUpsEnabled,
    ],
  )

  const { hasErrors, isAnyFieldEmpty, errors } = useCheckFormErrors(
    fields,
    rules,
    getRequiredFields(),
  )

  const hasPushNotCompleted = pushMessage && (!pushTitle || !pushDescription)

  const isSubmitDisabled =
    hasErrors ||
    isAnyFieldEmpty ||
    isLoading ||
    isUploadingPopUpImage ||
    hasPushNotCompleted ||
    hasPartnerErrors ||
    !hasValidSegmentation()

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return

    const requiredFields = getRequiredFields()
    const emptyRequiredFields = requiredFields.filter(fieldName => {
      const fieldValue = fields[fieldName as keyof typeof fields]
      const fieldRules = rules[fieldName as keyof typeof rules] as
        | Array<{ validateEmpty?: boolean }>
        | undefined
      const validateEmpty = fieldRules?.some(rule => rule.validateEmpty) ?? false
      return !fieldValue && !validateEmpty
    })

    const validationErrors = Object.entries(errors)
      .filter(([, error]) => error.hasError)
      .map(([field, error]) => `${field}: ${error.message}`)

    const blockedReasons: string[] = []
    if (isLoading) blockedReasons.push('Cargando')
    if (hasErrors) blockedReasons.push('Errores de validación')
    if (isAnyFieldEmpty) blockedReasons.push('Campos requeridos vacíos')
    if (hasPushNotCompleted) blockedReasons.push('Push activa sin título/descripción')
    if (hasPartnerErrors) blockedReasons.push('Errores en integración Tucán/POS')
    if (!hasValidSegmentation()) blockedReasons.push('Segmentación incompleta')

    // eslint-disable-next-line no-console
    console.log('[CreateVoucherDialog] Estado del botón Crear', {
      habilitado: !isSubmitDisabled,
      blockedReasons,
      emptyRequiredFields,
      validationErrors,
      snapshot: {
        listingTitle: listingTitle.trim() || '(vacío)',
        shortTitle: shortTitle.trim() || '(vacío)',
        benefitTitle: benefitTitle.trim() || '(vacío)',
        additionalText: additionalText.trim() || '(vacío)',
        icon: icon ? icon.name : '(sin archivo)',
        prizeStartDate: prizeStartDate?.format('YYYY-MM-DD') ?? '(vacío)',
        prizeExpiration: prizeExpiration?.format('YYYY-MM-DD') ?? '(vacío)',
        tagsSelected: tagsSelected.length,
        emailChips: emailChips.length,
        csvEmailCount,
        pushMessage,
        pushTitle: pushTitle.trim() || '(vacío)',
        pushDescription: pushDescription.trim() || '(vacío)',
        toteatEnabled,
        tucanEnabled,
        hasPartnerErrors,
      },
    })
  }, [
    isSubmitDisabled,
    isLoading,
    hasErrors,
    isAnyFieldEmpty,
    hasPushNotCompleted,
    hasPartnerErrors,
    errors,
    fields,
    getRequiredFields,
    hasValidSegmentation,
    listingTitle,
    shortTitle,
    benefitTitle,
    additionalText,
    icon,
    prizeStartDate,
    prizeExpiration,
    tagsSelected,
    emailChips,
    csvEmailCount,
    pushMessage,
    pushTitle,
    pushDescription,
    toteatEnabled,
    tucanEnabled,
    emailCsvFile,
  ])

  const handleEmailCsvChange = async (file: File) => {
    try {
      const emails = await parseEmailsFromCsvFile(file)
      if (!emails.length) {
        snackbar.show('El archivo CSV no contiene emails válidos.')
        return
      }
      const normalizedEmails = mergeEmails(emails)
      setEmailCsvFile(file)
      setCsvEmails(normalizedEmails)
      setCsvEmailCount(normalizedEmails.length)
      setEmailChips([])
      clearTagSegmentation()
    } catch {
      snackbar.show('No se pudo leer el archivo CSV.')
    }
  }

  const handlePopUpImageChange = async (file: File) => {
    const localPreviewUrl = URL.createObjectURL(file)
    setPopUpImagePreview(localPreviewUrl)
    setIsUploadingPopUpImage(true)
    try {
      const { popUpImageId: uploadedId, popUpImageUrl } = await uploadPopUpImageApi(file)
      setPopUpImageId(uploadedId)
      if (popUpImageUrl) {
        URL.revokeObjectURL(localPreviewUrl)
        setPopUpImagePreview(popUpImageUrl)
      }
    } catch (err) {
      console.error(err)
      snackbar.show('No se pudo subir la imagen del pop-up. Intente de nuevo.')
      URL.revokeObjectURL(localPreviewUrl)
      setPopUpImagePreview('')
      setPopUpImageId('')
    } finally {
      setIsUploadingPopUpImage(false)
    }
  }

  const buildPreloadedEmails = (): string | undefined => {
    if (csvEmails.length) return mergeEmails(csvEmails).join('\n')
    if (emailChips.length) return mergeEmails(emailChips).join('\n')
    return undefined
  }

  const onCreateVoucher = async () => {
    setHasAttemptedSubmit(true)
    if (!icon || !prizeStartDate || !prizeExpiration) return
    if (!hasValidSegmentation()) {
      snackbar.show('La segmentación es requerida.')
      return
    }
    if (pushMessage && hasPushNotCompleted) {
      snackbar.show('Todos los campos de la push son requeridos.')
      return
    }
    if (campaignPopUpsEnabled && activePopUp && !popUpImageId.trim()) {
      snackbar.show('La imagen del pop-up es requerida.')
      return
    }

    const usingTags = tagsSelected.length > 0 || excludedTagsSelected.length > 0
    const preloadedEmails = buildPreloadedEmails()

    try {
      let notificationId: number | undefined

      if (pushMessage) {
        const tagsSelectedId = usingTags ? tagsSelected.map(tag => tag.value) : []
        const scheduled = prizeStartDate.isAfter(moment())
          ? prizeStartDate.toISOString()
          : moment()
              .add(1, 'minute')
              .toISOString()

        const notificationPayload: CreateNotificationPayload = {
          title: pushTitle,
          body: pushDescription,
          scheduled,
          tagIds: tagsSelectedId,
          allTagsAreRequired: usingTags ? allTagsAreRequired : false,
          visitsOperator: '==',
          rewardInverted: false,
        }

        const { notification } = await createNotificationApi(notificationPayload)
        notificationId = notification.id
      }

      const voucherPayload: CreateVoucherPayload = {
        listingTitle,
        shortTitle,
        title: benefitTitle,
        additionalText,
        icon,
        image,
        pushMessage,
        pushTitle,
        pushDescription,
        notificationId,
        active,
        tagsSelected: usingTags ? tagsSelected : [],
        excludedTagsSelected: usingTags ? excludedTagsSelected : [],
        allTagsAreRequired: usingTags ? allTagsAreRequired : false,
        preloadedEmails,
        prizeStartDate: prizeStartDate.toISOString(),
        prizeExpiration: prizeExpiration.toISOString(),
        tucanCategory,
        toteatCategory,
        storesEnabled,
        tucanEnabled,
        toteatEnabled,
        skus: skusList,
        toteatBenefitId,
        visitsOperator: '==',
        rewardInverted: false,
        ...(campaignPopUpsEnabled
          ? {
              activePopUp,
              popUpImageId: activePopUp ? popUpImageId : undefined,
            }
          : {}),
      }

      const { voucher } = await createVoucherApi(voucherPayload)
      onDone(voucher)
    } catch (err) {
      console.error(err)
      snackbar.show('No se pudo crear el voucher. Intente de nuevo.')
    }
  }

  useEffect(() => {
    if (isEcardEnabled) {
      const file = new File([defaultIcon], 'default-icon.png', { type: 'image/png' })
      setIcon(file)
      setImage(file)
      setIconPreview(URL.createObjectURL(file))
      setImagePreview(URL.createObjectURL(file))
    }
  }, [isEcardEnabled])

  return (
    <Dialog
      title="Nuevo voucher"
      isOpen
      maxWidth={false}
      showTabsActions
      onCancel={handleClose}
      cancelButtonText="Cancelar"
      isLoading={isLoading}
      okButtonText="Crear"
      okButtonProps={{ disabled: isSubmitDisabled, startIcon: <AddIcon /> }}
      onAccept={onCreateVoucher}
      tabs={tabs}
      tabIndex={tabIndex}
      setTabIndex={setTabIndex}
      showUserCount={tagsSelected.length > 0 || excludedTagsSelected.length > 0}
      isRequestingData={isRequestingData}
      userCount={userCount ?? 0}
      style={campaignDialogStyle}
      contentStyle={voucherDialogContentStyle}
    >
      <Grid>
        <TabPanel value={tabIndex} index={0} id="voucher">
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <CustomTextField
                label="Título*"
                value={listingTitle}
                fullWidth
                onChange={e => setListingTitle(e.target.value)}
                disabled={isLoading}
                required
                maxLength={70}
                error={hasAttemptedSubmit && errors.listingTitle?.hasError}
                helperText={hasAttemptedSubmit ? errors.listingTitle?.message : ''}
              />
            </Grid>
            <Grid item xs={12}>
              <CustomTextField
                label="Descripción*"
                value={shortTitle}
                fullWidth
                onChange={e => setShortTitle(e.target.value)}
                disabled={isLoading}
                multiline
                rows={3}
                required
                maxLength={40}
                error={hasAttemptedSubmit && errors.shortTitle?.hasError}
                helperText={hasAttemptedSubmit ? errors.shortTitle?.message : ''}
              />
            </Grid>
            <Grid item xs={12}>
              <PrizeDateRangeFields
                startDate={prizeStartDate}
                expiration={prizeExpiration}
                onStartDateChange={setPrizeStartDate}
                onExpirationChange={setPrizeExpiration}
                disabled={isLoading}
                requiredStartDate
                requiredExpiration
              />
            </Grid>
            <Grid item xs={12}>
              <CustomFormControlLabel
                control={
                  <CustomCheckbox
                    onChange={() => setActive(!active)}
                    checked={active}
                    disabled={isLoading || isMarketing}
                  />
                }
                label="Habilitar voucher"
              />
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabIndex} index={1} id="segmentar">
          <VoucherSegmentationTab
            tagsForSelect={tagsForSelect}
            tagsSelected={tagsSelected}
            onTagsChange={handleSetTags}
            excludedTagsSelected={excludedTagsSelected}
            onExcludedTagsChange={handleSetExcludedTags}
            allTagsAreRequired={allTagsAreRequired}
            onAllTagsAreRequiredChange={handleAllTagsAreRequiredChange}
            emailChips={emailChips}
            onEmailChipsChange={handleEmailChipsChange}
            onEmailCsvChange={handleEmailCsvChange}
            onEmailCsvClear={clearEmailSegmentation}
            emailCsvFile={emailCsvFile}
            csvEmailCount={csvEmailCount}
            isLoading={isLoading}
          />
        </TabPanel>

        <TabPanel value={tabIndex} index={2} id="beneficio">
          <Grid container spacing={3}>
            <Grid item xs={12} className={classes.topCheckbox}>
              <CustomFormControlLabel
                control={<CustomCheckbox checked disabled />}
                label="Activar Reward"
              />
              <Typography
                variant="caption"
                display="block"
                style={{ marginLeft: 40, marginTop: 4 }}
              >
                Los vouchers son siempre de uso único.
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <CustomTextField
                label="Título*"
                value={benefitTitle}
                fullWidth
                onChange={e => setBenefitTitle(e.target.value)}
                disabled={isLoading}
                required
                maxLength={70}
                error={hasAttemptedSubmit && errors.benefitTitle?.hasError}
                helperText={hasAttemptedSubmit ? errors.benefitTitle?.message : ''}
              />
            </Grid>

            <Grid item xs={12}>
              <CustomTextField
                label="Título secundario*"
                value={additionalText}
                fullWidth
                onChange={e => setAdditionalText(e.target.value)}
                disabled={isLoading}
                required
                maxLength={100}
                error={hasAttemptedSubmit && errors.additionalText?.hasError}
                helperText={hasAttemptedSubmit ? errors.additionalText?.message : ''}
              />
            </Grid>

            {!isEcardEnabled && (
              <Grid item xs={12}>
                <Grid container className={classes.iconUploadContainer}>
                  <Grid item xs={6} className={classes.iconUploadButton}>
                    <UploadButton
                      id="voucher-icon-upload-button"
                      accept=".jpg, .jpeg, .png"
                      label="Subir icono del reward*"
                      onChange={file => {
                        setIcon(file)
                        setIconPreview(URL.createObjectURL(file))
                      }}
                      maxSizeInMB={2}
                      disabled={isLoading}
                    />
                    {hasAttemptedSubmit && errors.icon?.hasError && (
                      <Typography
                        variant="caption"
                        color="error"
                        style={{ marginTop: 4, display: 'block' }}
                      >
                        El icono es requerido
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    <div className={classes.iconUploadPreviewContainer}>
                      {iconPreview && (
                        <img src={iconPreview} alt="" className={classes.iconPreview} />
                      )}
                    </div>
                  </Grid>
                </Grid>
              </Grid>
            )}

            {hasAnyIntegration && (
              <>
                <Grid item xs={12}>
                  <Typography className={classes.dialogSubTitle} style={{ marginTop: 20 }}>
                    Integración
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <PartnerIntegration
                    hideTitle
                    storesEnabled={storesEnabled}
                    setStoresEnabled={setStoresEnabled}
                    tucanEnabled={tucanEnabled}
                    setTucanEnabled={setTucanEnabled}
                    toteatEnabled={toteatEnabled}
                    setToteatEnabled={setToteatEnabled}
                    tucanCategory={tucanCategory}
                    setTucanCategory={setTucanCategory}
                    toteatCategory={toteatCategory}
                    setToteatCategory={setToteatCategory}
                    currentSku={currentSku}
                    setCurrentSku={setCurrentSku}
                    skusList={skusList}
                    setSkusList={setSkusList}
                    toteatBenefitId={toteatBenefitId}
                    setToteatBenefitId={setToteatBenefitId}
                    errors={errors}
                    isLoading={isLoading}
                    onValidationChange={setHasPartnerErrors}
                    showErrors={hasAttemptedSubmit}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </TabPanel>

        <TabPanel value={tabIndex} index={3} id="push">
          <Grid container spacing={3}>
            <Grid item xs={12} className={classes.topCheckbox}>
              <CustomFormControlLabel
                control={
                  <CustomCheckbox
                    onChange={() => setPushMessage(!pushMessage)}
                    checked={pushMessage}
                  />
                }
                label="Sale con push?"
              />
            </Grid>

            {pushMessage && (
              <>
                <Grid item xs={12}>
                  <CustomTextField
                    label="Título push*"
                    value={pushTitle}
                    fullWidth
                    required
                    maxLength={30}
                    error={hasAttemptedSubmit && errors.pushTitle?.hasError}
                    helperText={hasAttemptedSubmit ? errors.pushTitle?.message : ''}
                    onChange={e => setPushTitle(e.target.value)}
                    disabled={isLoading}
                  />
                </Grid>
                <Grid item xs={12}>
                  <CustomTextField
                    label="Descripción push*"
                    value={pushDescription}
                    fullWidth
                    required
                    multiline
                    maxLength={100}
                    error={hasAttemptedSubmit && errors.pushDescription?.hasError}
                    helperText={hasAttemptedSubmit ? errors.pushDescription?.message : ''}
                    onChange={e => setPushDescription(e.target.value)}
                    disabled={isLoading}
                  />
                </Grid>
                {!isEcardEnabled && shouldShowPushImageUpload() && (
                  <Grid item xs={12}>
                    <Grid container className={classes.iconUploadContainer}>
                      <Grid item xs={6} className={classes.iconUploadButton}>
                        <UploadButton
                          id="voucher-push-image-upload-button"
                          accept=".jpg, .jpeg, .png"
                          label="Subir imagen de la push"
                          onChange={file => {
                            setImage(file)
                            setImagePreview(URL.createObjectURL(file))
                          }}
                          maxSizeInMB={2}
                          disabled={isLoading}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <div className={classes.iconUploadPreviewContainer}>
                          {imagePreview && (
                            <img src={imagePreview} alt="" className={classes.iconPreview} />
                          )}
                        </div>
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              </>
            )}
          </Grid>
        </TabPanel>

        {campaignPopUpsEnabled && (
          <TabPanel value={tabIndex} index={popUpTabIndex} id="pop-up">
            <Grid container spacing={3}>
              <Grid item xs={12} className={classes.topCheckbox}>
                <CustomFormControlLabel
                  control={
                    <CustomCheckbox
                      onChange={e => setActivePopUp(e.target.checked)}
                      checked={activePopUp}
                    />
                  }
                  label="Activar Pop-up"
                />
              </Grid>

              {activePopUp && (
                <Grid item xs={12}>
                  <Grid container className={classes.iconUploadContainer}>
                    <Grid item xs={6} className={classes.iconUploadButton}>
                      <UploadButton
                        id="voucher-pop-up-image-upload-button"
                        accept=".jpg, .jpeg, .png, .webp"
                        label="Subir imagen del pop-up*"
                        onChange={handlePopUpImageChange}
                        maxSizeInMB={2}
                        disabled={isLoading || isUploadingPopUpImage}
                      />
                      <Typography variant="caption" display="block" style={{ marginTop: 4 }}>
                        Formato vertical recomendado 9:16. Máximo 2MB (PNG, JPG, WebP).
                      </Typography>
                      {hasAttemptedSubmit && errors.popUpImageId?.hasError && (
                        <Typography
                          variant="caption"
                          color="error"
                          style={{ marginTop: 4, display: 'block' }}
                        >
                          {errors.popUpImageId.message}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      <div className={classes.iconUploadPreviewContainer}>
                        {popUpImagePreview && (
                          <img
                            src={popUpImagePreview}
                            alt=""
                            className={classes.iconPreview}
                            style={{ aspectRatio: '9 / 16', objectFit: 'cover' }}
                          />
                        )}
                      </div>
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </TabPanel>
        )}
      </Grid>
    </Dialog>
  )
}

export { CreateVoucherDialog }
